"use client";

import { fetchFile } from "@ffmpeg/util";
import {
	AlertTriangle,
	AudioWaveform,
	Download,
	Loader,
	Music,
	Pause,
	Play,
	Settings,
	Upload,
	Volume2,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import type WaveSurfer from "wavesurfer.js";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useFFmpeg } from "@/contexts/FFmpegContext";
import { FileDropZone } from "@/components/FileDropZone";
import logger from "@/lib/logger";
import { useTranslations } from "next-intl";

// Define available audio output formats with quality options
const AUDIO_FORMATS = [
	// --- HIGH QUALITY FORMATS ---
	{
		label: "MP3 (320k - High Quality)",
		ext: "mp3",
		mime: "audio/mp3",
		icon: Music,
		command: ["-vn", "-c:a", "libmp3lame", "-b:a", "320k"],
		quality: "high",
	},
	{
		label: "MP3 (192k - Standard)",
		ext: "mp3",
		mime: "audio/mp3",
		icon: Music,
		command: ["-vn", "-c:a", "libmp3lame", "-b:a", "192k"],
		quality: "standard",
	},
	{
		label: "MP3 (128k - Good)",
		ext: "mp3",
		mime: "audio/mp3",
		icon: Music,
		command: ["-vn", "-c:a", "libmp3lame", "-b:a", "128k"],
		quality: "good",
	},
	{
		label: "AAC (256k - High Quality)",
		ext: "aac",
		mime: "audio/aac",
		icon: Music,
		command: ["-vn", "-c:a", "aac", "-b:a", "256k"],
		quality: "high",
	},
	{
		label: "AAC (128k - Standard)",
		ext: "aac",
		mime: "audio/aac",
		icon: Music,
		command: ["-vn", "-c:a", "aac", "-b:a", "128k"],
		quality: "standard",
	},
	// --- LOSSLESS FORMATS ---
	{
		label: "FLAC (Lossless)",
		ext: "flac",
		mime: "audio/flac",
		icon: Music,
		command: ["-vn", "-c:a", "flac"],
		quality: "lossless",
	},
	{
		label: "WAV (Uncompressed)",
		ext: "wav",
		mime: "audio/wav",
		icon: Music,
		command: ["-vn", "-c:a", "pcm_s16le"],
		quality: "lossless",
	},
	// --- OTHER FORMATS ---
	{
		label: "OGG Vorbis (192k)",
		ext: "ogg",
		mime: "audio/ogg",
		icon: Music,
		command: ["-vn", "-c:a", "libvorbis", "-b:a", "192k"],
		quality: "standard",
	},
	{
		label: "OGG Vorbis (320k - High Quality)",
		ext: "ogg",
		mime: "audio/ogg",
		icon: Music,
		command: ["-vn", "-c:a", "libvorbis", "-b:a", "320k"],
		quality: "high",
	},
	{
		label: "M4A (AAC 192k)",
		ext: "m4a",
		mime: "audio/mp4",
		icon: Music,
		command: ["-vn", "-c:a", "aac", "-b:a", "192k"],
		quality: "standard",
	},
];

// Define audio processing options
interface AudioProcessingOptions {
	audioDenoising: {
		enabled: boolean;
		strength: number; // noise reduction in dB (1 to 50)
	};
	volumeNormalization: {
		enabled: boolean;
		target: number; // target loudness in LUFS (-30 to -10)
	};
	silenceRemoval: {
		enabled: boolean;
		threshold: number; // in dB (-60 to -10)
		duration: number; // minimum silence duration in seconds (0.1 to 5)
	};
	bassBoost: {
		enabled: boolean;
		frequency: number; // bass frequency (20 to 200 Hz)
		gain: number; // gain in dB (0 to 20)
	};
	trebleBoost: {
		enabled: boolean;
		frequency: number; // treble frequency (2000 to 8000 Hz)
		gain: number; // gain in dB (0 to 20)
	};
	sampleRate: {
		enabled: boolean;
		rate: number; // target sample rate (22050, 44100, 48000, 96000)
	};
	channels: {
		enabled: boolean;
		count: number; // 1 (mono) or 2 (stereo)
	};
}

const DEFAULT_AUDIO_OPTIONS: AudioProcessingOptions = {
	audioDenoising: {
		enabled: false,
		strength: 12,
	},
	volumeNormalization: {
		enabled: false,
		target: -16,
	},
	silenceRemoval: {
		enabled: false,
		threshold: -30,
		duration: 1.0,
	},
	bassBoost: {
		enabled: false,
		frequency: 100,
		gain: 5,
	},
	trebleBoost: {
		enabled: false,
		frequency: 5000,
		gain: 3,
	},
	sampleRate: {
		enabled: false,
		rate: 44100,
	},
	channels: {
		enabled: false,
		count: 2,
	},
};

const AudioExtractor = () => {
	const t = useTranslations("audioExtractor");
	const { ffmpegInstance, isLoaded, isLoading, hardwareInfo, error, loadFFmpeg } =
		useFFmpeg();

	useEffect(() => {
		loadFFmpeg();
	}, [loadFFmpeg]);

	const [inputFile, setInputFile] = useState<File | null>(null);
	const [selectedFormat, setSelectedFormat] = useState(AUDIO_FORMATS[0]);
	const [outputURL, setOutputURL] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [statusLog, setStatusLog] = useState("");
	const [progress, setProgress] = useState(0);
	const [startTime, setStartTime] = useState<number | null>(null);
	const [estimatedTimeRemaining, setEstimatedTimeRemaining] =
		useState<string>("");
	const [audioOptions, setAudioOptions] = useState<AudioProcessingOptions>(
		DEFAULT_AUDIO_OPTIONS,
	);
	const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
	const [isClient, setIsClient] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [audioDuration, setAudioDuration] = useState(0);
	const waveformRef = useRef<HTMLDivElement>(null);
	const wavesurferRef = useRef<WaveSurfer | null>(null);

	// Track client-side mounting
	useEffect(() => {
		setIsClient(true);
	}, []);

	// Initialize WaveSurfer for audio preview
	const initializeWaveSurfer = useCallback(async (audioUrl: string) => {
		if (!isClient || !waveformRef.current) return;

		try {
			// Clean up existing wavesurfer instance
			if (wavesurferRef.current) {
				wavesurferRef.current.destroy();
				wavesurferRef.current = null;
			}

			// Dynamically import WaveSurfer to reduce initial bundle size
			const WaveSurferModule = await import("wavesurfer.js");
			const WaveSurfer = WaveSurferModule.default;

			// Create new wavesurfer instance
			wavesurferRef.current = WaveSurfer.create({
				container: waveformRef.current,
				waveColor: '#3b82f6',
				progressColor: '#1d4ed8',
				cursorColor: '#ef4444',
				barWidth: 2,
				barRadius: 3,
				height: 120,
				normalize: true,
				fillParent: true,
				mediaControls: false,
				interact: true
			});

			// Set up event listeners
			wavesurferRef.current.on('ready', () => {
				if (wavesurferRef.current) {
					const duration = wavesurferRef.current.getDuration();
					setAudioDuration(duration);
				}
			});

			wavesurferRef.current.on('audioprocess', (time) => {
				setCurrentTime(time);
			});

			wavesurferRef.current.on('play', () => {
				setIsPlaying(true);
			});

			wavesurferRef.current.on('pause', () => {
				setIsPlaying(false);
			});

			wavesurferRef.current.on('seeking', (progress: number) => {
				if (wavesurferRef.current && progress !== undefined) {
					const time = progress * wavesurferRef.current.getDuration();
					setCurrentTime(time);
				}
			});

			wavesurferRef.current.on('error', (error) => {
				logger.error({ err: error }, 'WaveSurfer error');
			});

			// Load audio file after setting up listeners
			wavesurferRef.current.load(audioUrl);
		} catch (error) {
			logger.error({ err: error }, 'Failed to initialize WaveSurfer');
		}
	}, [isClient]);

	// Audio playback controls
	const togglePlayPause = useCallback(() => {
		if (wavesurferRef.current) {
			if (isPlaying) {
				wavesurferRef.current.pause();
			} else {
				wavesurferRef.current.play().catch((err) => logger.error({ err }, "Audio playback error"));
			}
		}
	}, [isPlaying]);

	// Format time helper
	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		const ms = Math.floor((seconds % 1) * 100);
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
	};

	// Cleanup wavesurfer on unmount
	useEffect(() => {
		return () => {
			if (wavesurferRef.current) {
				try {
					wavesurferRef.current.destroy();
				} catch (error) {
					logger.warn({ err: error }, 'Error destroying WaveSurfer instance');
				} finally {
					wavesurferRef.current = null;
				}
			}
		};
	}, []);

	// Initialize WaveSurfer when output audio is available
	useEffect(() => {
		if (outputURL && isClient) {
			// Small delay to ensure DOM is ready
			const timer = setTimeout(() => {
				initializeWaveSurfer(outputURL);
			}, 100);
			
			return () => clearTimeout(timer);
		}
	}, [outputURL, isClient, initializeWaveSurfer]);

	// Helper function to calculate estimated time remaining
	const calculateTimeRemaining = useCallback(
		(currentProgress: number, startTime: number): string => {
			if (currentProgress <= 0) return "";

			const elapsed = Date.now() - startTime;
			const totalEstimated = elapsed / currentProgress;
			const remaining = totalEstimated - elapsed;

			if (remaining <= 0) return t("progress.almostDone");

			const minutes = Math.floor(remaining / 60000);
			const seconds = Math.floor((remaining % 60000) / 1000);

			if (minutes > 0) {
				return t("progress.minutesRemaining", { minutes, seconds });
			}
			return t("progress.secondsRemaining", { seconds });
		},
		[t],
	);

	// Helper function to build audio processing filters
	const buildAudioFilters = (options: AudioProcessingOptions): string[] => {
		const audioFilters: string[] = [];

		// Audio denoising filter
		if (options.audioDenoising.enabled) {
			audioFilters.push(`afftdn=nr=${options.audioDenoising.strength}`);
		}

		// Silence removal filter
		if (options.silenceRemoval.enabled) {
			audioFilters.push(
				`silenceremove=start_periods=1:stop_periods=-1:start_threshold=${options.silenceRemoval.threshold}dB:stop_threshold=${options.silenceRemoval.threshold}dB:start_silence=${options.silenceRemoval.duration}:stop_silence=${options.silenceRemoval.duration}`,
			);
		}

		// Bass boost filter
		if (options.bassBoost.enabled) {
			audioFilters.push(
				`bass=frequency=${options.bassBoost.frequency}:gain=${options.bassBoost.gain}`,
			);
		}

		// Treble boost filter
		if (options.trebleBoost.enabled) {
			audioFilters.push(
				`treble=frequency=${options.trebleBoost.frequency}:gain=${options.trebleBoost.gain}`,
			);
		}

		// Volume normalization filter (should be last in the chain)
		if (options.volumeNormalization.enabled) {
			audioFilters.push(`loudnorm=I=${options.volumeNormalization.target}`);
		}

		const filters: string[] = [];
		if (audioFilters.length > 0) {
			filters.push("-af", audioFilters.join(","));
		}

		// Sample rate conversion
		if (options.sampleRate.enabled) {
			filters.push("-ar", options.sampleRate.rate.toString());
		}

		// Channel conversion
		if (options.channels.enabled) {
			filters.push("-ac", options.channels.count.toString());
		}

		return filters;
	};

	// Update status based on FFmpeg loading state
	useEffect(() => {
		if (error) {
			setStatusLog(error);
		} else if (isLoading) {
			setStatusLog(t("status.loading"));
		} else if (isLoaded) {
			setStatusLog(t("status.ready"));
		} else {
			setStatusLog(t("status.initializing"));
		}
	}, [error, isLoading, isLoaded]);

	// Set up FFmpeg progress listener
	useEffect(() => {
		if (ffmpegInstance && isProcessing) {
			const handleProgress = ({ progress }: { progress: number }) => {
				setProgress(progress);
				if (startTime) {
					setEstimatedTimeRemaining(
						calculateTimeRemaining(progress, startTime),
					);
				}
			};

			ffmpegInstance.on("progress", handleProgress);

			return () => {
				ffmpegInstance.off("progress", handleProgress);
			};
		}
	}, [ffmpegInstance, isProcessing, startTime, calculateTimeRemaining]);

	// Handle file selection
	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			setInputFile(file);
			setOutputURL("");
			setStatusLog(
				t("status.fileSelected", { name: file.name, format: selectedFormat.ext.toUpperCase() }),
			);
		}
	};

	// Core audio extraction logic
	const extractAudio = useCallback(async () => {
		if (!inputFile || !ffmpegInstance) return;

		setIsProcessing(true);
		setOutputURL("");
		setProgress(0);
		setStartTime(Date.now());
		setEstimatedTimeRemaining("");
		setStatusLog(
			t("status.extracting", { name: inputFile.name, format: selectedFormat.ext.toUpperCase() }),
		);

		const inputFileName = `input.${inputFile.name.split(".").pop()}`;
		const outputFileName = `audio.${selectedFormat.ext}`;

		try {
			// Check file size for memory management
			const fileSizeMB = inputFile.size / (1024 * 1024);
			const memoryLimitMB = hardwareInfo.memory * 1024;

			if (hardwareInfo.isLowEnd && fileSizeMB > memoryLimitMB * 0.3) {
				throw new Error(
					t("errors.fileTooLarge", { size: fileSizeMB.toFixed(1) }),
				);
			}

			setStatusLog(t("status.loadingFile"));

			// Write file to FFmpeg filesystem
			try {
				await ffmpegInstance.writeFile(
					inputFileName,
					await fetchFile(inputFile),
				);
			} catch (writeError) {
				if (
					writeError instanceof Error &&
					writeError.message.includes("memory")
				) {
					throw new Error(
						t("errors.outOfMemoryLoad"),
					);
				}
				throw writeError;
			}

			const baseCommand = ["-i", inputFileName];

			// Build audio processing filters
			const audioFilters = buildAudioFilters(audioOptions);

			// Build final command
			const fullCommand = [
				...baseCommand,
				...audioFilters,
				...selectedFormat.command,
				outputFileName,
			];

			setStatusLog(t("status.processingAudio"));

			logger.debug({ fullCommand }, "Audio extraction command");

			try {
				await ffmpegInstance.exec(fullCommand);
			} catch (execError) {
				if (
					execError instanceof Error &&
					(execError.message.includes("memory") ||
						execError.message.includes("Out of memory") ||
						execError.message.includes("RangeError"))
				) {
					throw new Error(
						t("errors.outOfMemoryConvert"),
					);
				}
				throw execError;
			}

			setStatusLog(t("status.readingFile"));

			// Read output file
			let data: Uint8Array;
			try {
				data = (await ffmpegInstance.readFile(outputFileName)) as Uint8Array;
			} catch (readError) {
				if (
					readError instanceof Error &&
					readError.message.includes("memory")
				) {
					throw new Error(
						t("errors.outOfMemoryRead"),
					);
				}
				throw readError;
			}

			// Create downloadable blob
			try {
				const blob = new Blob([data as BlobPart], {
					type: selectedFormat.mime,
				});
				const url = URL.createObjectURL(blob);
				setOutputURL(url);
				setProgress(1);
				setEstimatedTimeRemaining("");

				setStatusLog(
					t("status.extractionComplete", { format: selectedFormat.ext.toUpperCase() }),
				);
			} catch {
				throw new Error(
					t("errors.outOfMemoryDownload"),
				);
			}
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error occurred";
			setStatusLog(t("status.extractionFailed", { error: errorMessage }));
			logger.error({ err: error }, "FFmpeg Audio Extraction Error");
			setProgress(0);
			setEstimatedTimeRemaining("");
		} finally {
			setIsProcessing(false);
			// Cleanup filesystem
			try {
				await ffmpegInstance.deleteFile(inputFileName);
				await ffmpegInstance.deleteFile(outputFileName);
			} catch {
				// Files may not exist, ignore cleanup errors
			}
		}
	}, [inputFile, ffmpegInstance, selectedFormat, hardwareInfo, audioOptions, t]);

	// Helper component to display the selected format icon
	const FormatIcon = selectedFormat.icon;


	return (
		<div className="max-w-7xl mx-auto min-h-screen p-4 sm:p-8">
			<div className="mx-auto p-2 sm:p-6 md:p-10">
				<header className="text-center mb-10">
					<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
						<AudioWaveform
							className="h-8 w-8 me-4 text-destructive"
							aria-hidden="true"
						/>
						{t("title")}
					</h1>
					<p className="mt-4 text-muted-foreground text-lg font-bold">
						{t("subtitle")}
					</p>
				</header>

				{/* Status Bar */}
				<Alert
					className={`mb-6 ${isLoaded ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}
				>
					<AlertTriangle className="h-4 w-4" aria-hidden="true" />
					<AlertDescription className="font-bold">{statusLog}</AlertDescription>
				</Alert>

				{/* Progress Bar */}
				{isProcessing && (
					<Card className="mb-6 p-3 sm:p-6">
						<div className="flex justify-between items-center mb-4">
							<Badge variant="secondary" className="font-black text-lg">
								{t("progress.extracting", { format: selectedFormat.ext.toUpperCase() })}
							</Badge>
							<span className="text-lg font-black">
								{Math.round(progress * 100)}%
							</span>
						</div>
						<Progress
							value={progress * 100}
							className="mb-4"
							aria-label={t("progress.extractionProgress", { percent: Math.round(progress * 100) })}
						/>
						{estimatedTimeRemaining && (
							<div className="text-sm font-bold text-center text-muted-foreground">
								{estimatedTimeRemaining}
							</div>
						)}
					</Card>
				)}

				{/* Main Interface */}
				<div className="flex flex-col gap-6">
					{/* 1. Upload Section */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Upload className="w-6 h-6" aria-hidden="true" />
								{t("steps.selectFile")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<FileDropZone accept="video/*" disabled={!isLoaded || isProcessing} onFiles={(files) => handleFileChange({ target: { files } } as any)} label={t("fileInput.label")} activeLabel={t("fileInput.activeLabel")} />
							{inputFile && (
								<p id="file-info" className="mt-3 text-sm font-bold">
									<span className="text-primary">{inputFile.name}</span> (
									{(inputFile.size / 1024 / 1024).toFixed(2)} MB)
								</p>
							)}
							{!isLoaded && (
								<div className="mt-3 text-yellow-600 flex items-center font-bold">
									<Loader
										className="w-4 h-4 me-2 animate-spin"
										aria-hidden="true"
									/>
									{t("fileInput.loading")}
								</div>
							)}
							{!inputFile && isLoaded && (
								<p
									id="file-help"
									className="mt-2 text-sm text-muted-foreground font-bold"
								>
									{t("fileInput.hint")}
								</p>
							)}
						</CardContent>
					</Card>

					{/* 2. Audio Format Selection */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<FormatIcon className="w-6 h-6" aria-hidden="true" />
								{t("steps.selectFormat")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<label htmlFor="format-select" className="sr-only">
								{t("fileInput.srLabel")}
							</label>
							<Select
								value={AUDIO_FORMATS.indexOf(selectedFormat).toString()}
								onValueChange={(value) => {
									const formatIndex = parseInt(value);
									const format = AUDIO_FORMATS[formatIndex];
									if (format) {
										setSelectedFormat(format);
										setOutputURL("");
										setStatusLog(
											inputFile
												? t("status.formatChanged", { format: format.ext.toUpperCase() })
												: t("status.formatReady"),
										);
									}
								}}
								disabled={!isLoaded || isProcessing}
							>
								<SelectTrigger
									id="format-select"
									className="w-full"
									aria-describedby="format-help"
								>
									<SelectValue placeholder={t("formats.placeholder")} />
								</SelectTrigger>
								<SelectContent>
									{AUDIO_FORMATS.map((format, idx) => (
										<SelectItem key={idx} value={idx.toString()}>
											{format.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p
								id="format-help"
								className="mt-2 text-sm text-muted-foreground font-bold"
							>
								{t("formats.selected", { label: selectedFormat.label })}
							</p>

							{/* Quality Badge */}
							<div className="mt-4">
								<Badge
									variant={
										selectedFormat.quality === "lossless"
											? "default"
											: selectedFormat.quality === "high"
												? "secondary"
												: "outline"
									}
									className="font-bold"
								>
									{selectedFormat.quality === "lossless"
										? t("formats.qualityLossless")
										: selectedFormat.quality === "high"
											? t("formats.qualityHigh")
											: selectedFormat.quality === "standard"
												? t("formats.qualityStandard")
												: t("formats.qualityGood")}
								</Badge>
							</div>
						</CardContent>
					</Card>

					{/* 3. Advanced Audio Processing Options */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Settings className="w-6 h-6" aria-hidden="true" />
								{t("steps.processingOptions")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<div className="mb-4">
								<Button
									variant="outline"
									onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
									className="w-full"
									aria-expanded={showAdvancedOptions}
								>
									<Volume2 className="w-4 h-4 me-2" aria-hidden="true" />
									{showAdvancedOptions ? t("processing.hideAdvanced") : t("processing.showAdvanced")}
								</Button>
							</div>

							{showAdvancedOptions && (
								<div className="space-y-6">
									{/* Audio Denoising */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="audio-denoising"
												checked={audioOptions.audioDenoising.enabled}
												onCheckedChange={(checked) =>
													setAudioOptions((prev) => ({
														...prev,
														audioDenoising: {
															...prev.audioDenoising,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="audio-denoising"
												className="text-sm font-medium"
											>
												{t("processing.audioDenoising")}
											</Label>
										</div>
										{audioOptions.audioDenoising.enabled && (
											<div className="ms-6">
												<Label className="text-xs text-muted-foreground">
													{t("processing.denoisingStrength", { value: audioOptions.audioDenoising.strength })}
												</Label>
												<Slider
													value={[audioOptions.audioDenoising.strength]}
													onValueChange={([value]) =>
														setAudioOptions((prev) => ({
															...prev,
															audioDenoising: {
																...prev.audioDenoising,
																strength: value,
															},
														}))
													}
													min={1}
													max={50}
													step={1}
													className="mt-1"
												/>
											</div>
										)}
									</div>

									{/* Volume Normalization */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="volume-normalization"
												checked={audioOptions.volumeNormalization.enabled}
												onCheckedChange={(checked) =>
													setAudioOptions((prev) => ({
														...prev,
														volumeNormalization: {
															...prev.volumeNormalization,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="volume-normalization"
												className="text-sm font-medium"
											>
												{t("processing.volumeNormalization")}
											</Label>
										</div>
										{audioOptions.volumeNormalization.enabled && (
											<div className="ms-6">
												<Label className="text-xs text-muted-foreground">
													{t("processing.volumeTarget", { value: audioOptions.volumeNormalization.target })}
												</Label>
												<Slider
													value={[audioOptions.volumeNormalization.target]}
													onValueChange={([value]) =>
														setAudioOptions((prev) => ({
															...prev,
															volumeNormalization: {
																...prev.volumeNormalization,
																target: value,
															},
														}))
													}
													min={-30}
													max={-10}
													step={1}
													className="mt-1"
												/>
											</div>
										)}
									</div>

									{/* Silence Removal */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="silence-removal"
												checked={audioOptions.silenceRemoval.enabled}
												onCheckedChange={(checked) =>
													setAudioOptions((prev) => ({
														...prev,
														silenceRemoval: {
															...prev.silenceRemoval,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="silence-removal"
												className="text-sm font-medium"
											>
												{t("processing.silenceRemoval")}
											</Label>
										</div>
										{audioOptions.silenceRemoval.enabled && (
											<div className="ms-6 space-y-3">
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("processing.silenceThreshold", { value: audioOptions.silenceRemoval.threshold })}
													</Label>
													<Slider
														value={[audioOptions.silenceRemoval.threshold]}
														onValueChange={([value]) =>
															setAudioOptions((prev) => ({
																...prev,
																silenceRemoval: {
																	...prev.silenceRemoval,
																	threshold: value,
																},
															}))
														}
														min={-60}
														max={-10}
														step={1}
														className="mt-1"
													/>
												</div>
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("processing.silenceMinDuration", { value: audioOptions.silenceRemoval.duration })}
													</Label>
													<Slider
														value={[audioOptions.silenceRemoval.duration]}
														onValueChange={([value]) =>
															setAudioOptions((prev) => ({
																...prev,
																silenceRemoval: {
																	...prev.silenceRemoval,
																	duration: value,
																},
															}))
														}
														min={0.1}
														max={5}
														step={0.1}
														className="mt-1"
													/>
												</div>
											</div>
										)}
									</div>

									{/* Bass Boost */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="bass-boost"
												checked={audioOptions.bassBoost.enabled}
												onCheckedChange={(checked) =>
													setAudioOptions((prev) => ({
														...prev,
														bassBoost: {
															...prev.bassBoost,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="bass-boost"
												className="text-sm font-medium"
											>
												{t("processing.bassBoost")}
											</Label>
										</div>
										{audioOptions.bassBoost.enabled && (
											<div className="ms-6 space-y-3">
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("processing.bassFrequency", { value: audioOptions.bassBoost.frequency })}
													</Label>
													<Slider
														value={[audioOptions.bassBoost.frequency]}
														onValueChange={([value]) =>
															setAudioOptions((prev) => ({
																...prev,
																bassBoost: {
																	...prev.bassBoost,
																	frequency: value,
																},
															}))
														}
														min={20}
														max={200}
														step={5}
														className="mt-1"
													/>
												</div>
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("processing.bassGain", { value: audioOptions.bassBoost.gain })}
													</Label>
													<Slider
														value={[audioOptions.bassBoost.gain]}
														onValueChange={([value]) =>
															setAudioOptions((prev) => ({
																...prev,
																bassBoost: {
																	...prev.bassBoost,
																	gain: value,
																},
															}))
														}
														min={0}
														max={20}
														step={1}
														className="mt-1"
													/>
												</div>
											</div>
										)}
									</div>

									{/* Treble Boost */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="treble-boost"
												checked={audioOptions.trebleBoost.enabled}
												onCheckedChange={(checked) =>
													setAudioOptions((prev) => ({
														...prev,
														trebleBoost: {
															...prev.trebleBoost,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="treble-boost"
												className="text-sm font-medium"
											>
												{t("processing.trebleBoost")}
											</Label>
										</div>
										{audioOptions.trebleBoost.enabled && (
											<div className="ms-6 space-y-3">
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("processing.trebleFrequency", { value: audioOptions.trebleBoost.frequency })}
													</Label>
													<Slider
														value={[audioOptions.trebleBoost.frequency]}
														onValueChange={([value]) =>
															setAudioOptions((prev) => ({
																...prev,
																trebleBoost: {
																	...prev.trebleBoost,
																	frequency: value,
																},
															}))
														}
														min={2000}
														max={8000}
														step={100}
														className="mt-1"
													/>
												</div>
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("processing.trebleGain", { value: audioOptions.trebleBoost.gain })}
													</Label>
													<Slider
														value={[audioOptions.trebleBoost.gain]}
														onValueChange={([value]) =>
															setAudioOptions((prev) => ({
																...prev,
																trebleBoost: {
																	...prev.trebleBoost,
																	gain: value,
																},
															}))
														}
														min={0}
														max={20}
														step={1}
														className="mt-1"
													/>
												</div>
											</div>
										)}
									</div>

									{/* Sample Rate */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="sample-rate"
												checked={audioOptions.sampleRate.enabled}
												onCheckedChange={(checked) =>
													setAudioOptions((prev) => ({
														...prev,
														sampleRate: {
															...prev.sampleRate,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label
												htmlFor="sample-rate"
												className="text-sm font-medium"
											>
												{t("processing.convertSampleRate")}
											</Label>
										</div>
										{audioOptions.sampleRate.enabled && (
											<div className="ms-6">
												<Label className="text-xs text-muted-foreground">
													{t("processing.sampleRateLabel", { value: audioOptions.sampleRate.rate })}
												</Label>
												<Select
													value={audioOptions.sampleRate.rate.toString()}
													onValueChange={(value) =>
														setAudioOptions((prev) => ({
															...prev,
															sampleRate: {
																...prev.sampleRate,
																rate: parseInt(value),
															},
														}))
													}
												>
													<SelectTrigger className="mt-1">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="22050">{t("processing.sampleRate22050")}</SelectItem>
														<SelectItem value="44100">
															{t("processing.sampleRate44100")}
														</SelectItem>
														<SelectItem value="48000">
															{t("processing.sampleRate48000")}
														</SelectItem>
														<SelectItem value="96000">
															{t("processing.sampleRate96000")}
														</SelectItem>
													</SelectContent>
												</Select>
											</div>
										)}
									</div>

									{/* Channel Configuration */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="channels"
												checked={audioOptions.channels.enabled}
												onCheckedChange={(checked) =>
													setAudioOptions((prev) => ({
														...prev,
														channels: {
															...prev.channels,
															enabled: !!checked,
														},
													}))
												}
											/>
											<Label htmlFor="channels" className="text-sm font-medium">
												{t("processing.convertChannels")}
											</Label>
										</div>
										{audioOptions.channels.enabled && (
											<div className="ms-6">
												<Label className="text-xs text-muted-foreground">
													{t("processing.channelsLabel", {
														value: audioOptions.channels.count === 1
															? t("processing.channelsMono")
															: t("processing.channelsStereo")
													})}
												</Label>
												<Select
													value={audioOptions.channels.count.toString()}
													onValueChange={(value) =>
														setAudioOptions((prev) => ({
															...prev,
															channels: {
																...prev.channels,
																count: parseInt(value),
															},
														}))
													}
												>
													<SelectTrigger className="mt-1">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="1">{t("processing.mono")}</SelectItem>
														<SelectItem value="2">
															{t("processing.stereo")}
														</SelectItem>
													</SelectContent>
												</Select>
											</div>
										)}
									</div>

									<Alert>
										<AlertTriangle className="h-4 w-4" aria-hidden="true" />
										<AlertDescription className="text-xs">
											{t("processing.warning")}
										</AlertDescription>
									</Alert>
								</div>
							)}
						</CardContent>
					</Card>

					{/* 4. Extract Button */}
					<div className="flex justify-center">
						<Button
							onClick={extractAudio}
							size="lg"
							variant={
								isLoaded && inputFile && !isProcessing ? "default" : "outline"
							}
							disabled={!isLoaded || !inputFile || isProcessing}
							className="px-8 py-4 text-lg"
							aria-describedby="extract-help"
						>
							{isProcessing ? (
								<>
									<Loader
										className="w-5 h-5 me-3 animate-spin"
										aria-hidden="true"
									/>
									{t("convert.extracting", { format: selectedFormat.ext.toUpperCase() })}
								</>
							) : (
								<>
									<Play className="w-5 h-5 me-3" aria-hidden="true" />
									{t("convert.extractAudio")}
								</>
							)}
						</Button>
					</div>
					{!isLoaded || !inputFile ? (
						<p
							id="extract-help"
							className="text-center text-sm text-muted-foreground font-bold mt-2"
						>
							{!isLoaded
								? t("convert.waitingLoad")
								: t("convert.selectFileHint")}
						</p>
					) : null}

					{/* 5. Preview & Download Section */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Download className="w-6 h-6" aria-hidden="true" />
								{t("steps.previewDownload")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							{outputURL ? (
								<div className="space-y-6">
									{/* Audio Waveform Preview */}
									<div>
										<h4 className="text-md font-bold mb-3">{t("download.waveformTitle")}</h4>
										<div className="w-full h-32 bg-muted rounded-lg border-2 border-muted-foreground/20 overflow-hidden">
											{isClient ? (
												<div 
													ref={waveformRef}
													className="w-full h-full"
												/>
											) : (
												<div className="w-full h-full flex items-center justify-center text-muted-foreground">
													{t("download.loadingWaveform")}
												</div>
											)}
										</div>
										
										{/* Audio Controls */}
										<div className="flex items-center justify-center gap-4 mt-4">
											<Button
												onClick={togglePlayPause}
												variant="outline"
												size="lg"
												disabled={!wavesurferRef.current}
												aria-label={isPlaying ? t("download.pauseAudio") : t("download.playAudio")}
											>
												{isPlaying ? (
													<Pause className="w-5 h-5" />
												) : (
													<Play className="w-5 h-5" />
												)}
											</Button>
											<span className="text-sm font-mono">
												{formatTime(currentTime)} / {formatTime(audioDuration)}
											</span>
										</div>
									</div>

									{/* Download Section */}
									<div className="flex flex-col items-center justify-center text-center space-y-4 pt-4 border-t">
										<Badge
											variant="secondary"
											className="text-lg font-black px-4 py-2"
										>
											{t("download.completed")}
										</Badge>
										<Button asChild size="lg" className="px-6 py-3">
											<a
												href={outputURL}
												download={`extracted_audio.${selectedFormat.ext}`}
												aria-describedby="download-info"
											>
												<Download className="w-5 h-5 me-3" aria-hidden="true" />
												{t("download.downloadFile", { ext: selectedFormat.ext })}
											</a>
										</Button>
										<p
											id="download-info"
											className="text-sm text-muted-foreground font-bold"
										>
											{t("download.readyForDownload")}
										</p>
									</div>
								</div>
							) : (
								<div className="text-center min-h-[12rem] flex items-center justify-center">
									{isProcessing ? (
										<p className="text-primary font-bold text-lg">
											{t("download.processingMessage")}
										</p>
									) : (
										<p className="text-muted-foreground font-bold">
											{t("download.emptyMessage")}
										</p>
									)}
								</div>
							)}
						</CardContent>
					</Card>
				</div>

				{/* Information */}
				<Alert className="mt-8">
					<AlertTriangle className="h-4 w-4 text-green-600" aria-hidden="true" />
					<AlertDescription className="font-bold">
						{t.rich("privacy", {
							code: (chunks) => (
								<code className="bg-muted p-1 rounded font-mono text-sm">
									{chunks}
								</code>
							),
						})}
					</AlertDescription>
				</Alert>
			</div>
		</div>
	);
};

// Create a client-only wrapper to prevent hydration issues
const AudioExtractorClient = dynamic(() => Promise.resolve(AudioExtractor), {
	ssr: false,
	loading: () => (
		<div
			className="max-w-7xl mx-auto min-h-screen p-4 sm:p-8 flex items-center justify-center"
		>
			<div className="text-center">
				<div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
				<p className="text-lg font-bold">Loading...</p>
			</div>
		</div>
	),
});

export default AudioExtractorClient;
